import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ActionButton, Loader } from '@communityshare/ui-shared';
import { HOME_VIDEO_AUDIENCES } from '@/features/videos-common/audiences';
import { useStaffPlaylists, useUpdatePlaylistAudience } from '../hooks/useStaffPlaylists';
import useSingleFlight from '../hooks/useSingleFlight';
import getErrorMessage from '../api/errorMessages';
import FeatureDialog from './FeatureDialog';
import { inputClassName } from './FormRow';
import StatusMessage from './StatusMessage';

/**
 * Imported playlists with a select to change each one's audience. Each
 * audience can have only one playlist: choosing an audience held by another
 * playlist gets a 409 naming the holder, and staff confirm replacing it
 * (PATCH again with replaceExisting) or cancel, which puts the select back.
 *
 * The heading is focusable (tabIndex -1) so the home section's "no
 * playlist" notice can move focus here.
 */
PlaylistsSection.propTypes = {
  headingId: PropTypes.string.isRequired
};

export default function PlaylistsSection(props) {
  const { headingId } = props;
  const { t } = useTranslation();
  const playlistsQuery = useStaffPlaylists();
  const updateAudience = useUpdatePlaylistAudience();

  // The value a select shows while its change is saving or being confirmed.
  const [pending, setPending] = useState(null);
  // { playlist, audience, holder } while the replace confirmation is open
  const [conflict, setConflict] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const audienceLabel = (audience) => t(`staff_videos.audiences.${audience || 'none'}`);

  function savedMessage(playlist, audience) {
    return audience
      ? t('staff_videos.playlists.saved_assigned', { title: playlist.title, audience: audienceLabel(audience) })
      : t('staff_videos.playlists.saved_unassigned', { title: playlist.title });
  }

  function showError(playlist, error) {
    const { message, detail } = getErrorMessage(error, t);
    setFeedback({
      tone: 'error',
      title: t('staff_videos.playlists.save_error', { title: playlist.title }),
      detail: detail || message
    });
  }

  const changeAudience = useSingleFlight(async (playlist, audience) => {
    setFeedback(null);
    setPending({ id: playlist.id, audience });
    try {
      await updateAudience.mutateAsync({ id: playlist.id, audience });
      setFeedback({ tone: 'success', title: savedMessage(playlist, audience) });
      setPending(null);
    } catch (error) {
      const holder = error?.status === 409 ? error.body?.holder : null;
      if (holder) {
        // Keep the chosen value showing while staff decide.
        setConflict({ playlist, audience, holder });
        return;
      }
      showError(playlist, error);
      setPending(null);
    }
  });

  const confirmReplace = useSingleFlight(async () => {
    const { playlist, audience } = conflict;
    try {
      await updateAudience.mutateAsync({ id: playlist.id, audience, replaceExisting: true });
      setFeedback({ tone: 'success', title: savedMessage(playlist, audience) });
    } catch (error) {
      showError(playlist, error);
    } finally {
      setConflict(null);
      setPending(null);
    }
  });

  function cancelReplace() {
    if (updateAudience.isLoading) return;
    setConflict(null);
    setPending(null);
  }

  const playlists = playlistsQuery.data || [];
  const busy = updateAudience.isLoading || Boolean(conflict);

  let content;
  if (playlistsQuery.isLoading) {
    content = (
      <div className="py-6 flex justify-center" role="status" aria-label={t('staff_videos.playlists.loading')}>
        <Loader />
      </div>
    );
  } else if (playlistsQuery.isError) {
    const { message, detail } = getErrorMessage(playlistsQuery.error, t);
    content = (
      <StatusMessage tone="error" title={t('staff_videos.playlists.load_error')} detail={detail || message}>
        <div className="mt-2">
          <ActionButton
            variant="secondary"
            loading={playlistsQuery.isFetching}
            loadingLabel={t('staff_videos.playlists.loading')}
            onClick={() => playlistsQuery.refetch()}
          >
            {t('staff_videos.playlists.retry')}
          </ActionButton>
        </div>
      </StatusMessage>
    );
  } else if (playlists.length === 0) {
    content = (
      <p className="text-sm text-cs-gray-700 bg-white border border-cs-gray-200 rounded-cs-10 p-4">
        {t('staff_videos.playlists.empty')}
      </p>
    );
  } else {
    content = (
      <div className="overflow-x-auto bg-white border border-cs-gray-200 rounded-cs-10">
        <table className="min-w-full divide-y divide-cs-gray-200 text-left">
          <thead className="bg-cs-gray-50">
            <tr>
              {['title', 'audience', 'status'].map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="px-4 py-3 text-xs font-semibold text-cs-gray-700 whitespace-nowrap"
                >
                  {t(`staff_videos.playlists.columns.${column}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-cs-gray-100">
            {playlists.map((playlist) => {
              const value = pending && pending.id === playlist.id ? pending.audience : playlist.audience;
              const selectId = `staff-playlist-${playlist.id}-audience`;
              return (
                <tr key={playlist.id} data-testid="staff-playlist-row">
                  <td className="px-4 py-3 text-sm font-semibold text-cs-gray-900">{playlist.title}</td>
                  <td className="px-4 py-3">
                    <label htmlFor={selectId} className="sr-only">
                      {t('staff_videos.playlists.audience_label', { title: playlist.title })}
                    </label>
                    <select
                      id={selectId}
                      value={value || ''}
                      onChange={(event) => {
                        const next = event.target.value || null;
                        if (next !== (playlist.audience || null)) changeAudience(playlist, next);
                      }}
                      disabled={busy}
                      className={classNames(inputClassName(false), 'min-w-[12rem]')}
                    >
                      <option value="">{audienceLabel(null)}</option>
                      {HOME_VIDEO_AUDIENCES.map((audience) => (
                        <option key={audience} value={audience}>{audienceLabel(audience)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={classNames(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
                      playlist.isPublished ? 'bg-green-50 text-cs-gray-900' : 'bg-cs-gray-100 text-cs-gray-700'
                    )}>
                      {playlist.isPublished ? t('staff_videos.list.published') : t('staff_videos.list.unpublished')}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-col space-y-1">
        <h2 id={headingId} tabIndex={-1} className="text-xl font-semibold text-cs-gray-900 focus:outline-none">
          {t('staff_videos.playlists.heading')}
        </h2>
        <p className="text-sm text-cs-gray-500">{t('staff_videos.playlists.description')}</p>
      </div>

      {content}

      <div aria-live="polite">
        {updateAudience.isLoading && !conflict && (
          <p className="text-sm text-cs-gray-700">{t('staff_videos.playlists.saving')}</p>
        )}
        {feedback && !updateAudience.isLoading && (
          <StatusMessage tone={feedback.tone} title={feedback.title} detail={feedback.detail} />
        )}
      </div>

      {conflict && (
        <FeatureDialog
          title={t('staff_videos.playlists.replace_title')}
          onClose={cancelReplace}
        >
          <p className="mt-4 text-base text-cs-gray-900">
            {t('staff_videos.playlists.replace_message', {
              audience: audienceLabel(conflict.audience),
              holder: conflict.holder.title
            })}
          </p>
          <div className="mt-6 flex flex-col-reverse md:flex-row md:justify-between gap-3">
            <ActionButton variant="secondary" onClick={cancelReplace} disabled={updateAudience.isLoading}>
              {t('staff_videos.playlists.replace_cancel')}
            </ActionButton>
            <ActionButton
              variant="primary"
              loading={updateAudience.isLoading}
              loadingLabel={t('staff_videos.playlists.saving')}
              onClick={confirmReplace}
            >
              {t('staff_videos.playlists.replace_confirm')}
            </ActionButton>
          </div>
        </FeatureDialog>
      )}
    </section>
  );
}
